import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  Typography,
  Divider,
  IconButton,
  Box,
  Paper,
  Stack,
  useMediaQuery,
} from "@mui/material";

import { jwtDecode } from "jwt-decode";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";

import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";

/* import {
  getNotasByProduct,
  addNotaToProduct,
  updateNota,
  deleteNota,
} from "../../api/Estimates/estimatesApi"; */

import {
  getNotasByEntityWithUser,
  addNotaToEntity,
  uploadAdjuntosNota,
  updateNotaEntity,
  deleteNotaEntity,
} from "../../api/CriticalPath/criticalPathNotasApi";

import { getAdjuntosByNota } from "../../api/CriticalPath/criticalPathNotasApi";

import { getUsersWithAddNotesPermission } from "../../api/User/userApi";

import {
  adjuntoFileUrl,
  buildCreateNotaPayload,
  buildEditNotaUpdatePayload,
  buildNotasDialogClosePayload,
  canSaveNewNota,
  isImageAdjunto,
  shouldLoadNotas,
} from "./notasDialogUtils";

const NotasDialog = ({
  open,
  onClose,
  productId,
  idMuestra = null,
  idPresupuestoA3Pedido = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const API_URL = process.env.REACT_APP_API_URL;
  const { t } = useTranslation();

  const fieldSx = {
    mb: 1,
    width: "100%",
    minWidth: 0,
    "& .MuiInputBase-root": {
      backgroundColor: theme.palette.caja.main,
      borderRadius: 1.5,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
  };

  const noteRowSx = {
    mb: 1.5,
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    alignItems: { xs: "stretch", md: "flex-start" },
    gap: { xs: 1, md: 1.25 },
  };

  const noteMetaSx = {
    border: 1,
    borderColor: "divider",
    borderRadius: 2,
    p: 1.5,
    display: "flex",
    flexDirection: "column",
    flex: { xs: "none", md: "0 0 200px" },
    width: { xs: "100%", md: "auto" },
    minWidth: 0,
    bgcolor: "background.paper",
    boxShadow:
      theme.palette.mode === "dark"
        ? "none"
        : "0 1px 2px rgba(0,0,0,0.04)",
  };

  const noteBodySx = {
    border: 1,
    borderColor: "divider",
    borderRadius: 2,
    p: 1.5,
    flex: 1,
    minWidth: 0,
    width: "100%",
    bgcolor: "background.paper",
    boxShadow:
      theme.palette.mode === "dark"
        ? "none"
        : "0 1px 2px rgba(0,0,0,0.04)",
  };
  const [nota, setNota] = useState("");
  const [notas, setNotas] = useState([]);
  const [editMode, setEditMode] = useState(null);
  const [editNota, setEditNota] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [notaToDelete, setNotaToDelete] = useState(null);

  const [tipoNota, setTipoNota] = useState("Remark");
  const [writer, setWriter] = useState("");
  const [responsible, setResponsible] = useState("");
  const [status, setStatus] = useState("Open");
  const [targetDate, setTargetDate] = useState(null);
  const [closingDate, setClosingDate] = useState(null);

  // Subir notas
  const [files, setFiles] = useState([]);
  const [adjuntos, setAdjuntos] = useState({});

  // Carga usuarios para escribir notas
  const [users, setUsers] = useState([]);

  // Necesitamos congelar los IDs dentro del Dialog cuando se abre.
  /*   const [entityIds, setEntityIds] = useState({
    productId: null,
    idMuestra: null,
  }); */

  /*   useEffect(() => {
    if (open) {
      setEntityIds({
        productId,
        idMuestra,
      });
    }
  }, [open, productId, idMuestra]); */

  useEffect(() => {
    if (open) loadUsers();
  }, [open]);

  const loadUsers = async () => {
    const data = await getUsersWithAddNotesPermission();
    setUsers(data);
  };

  useEffect(() => {
    if (users.length && !writer) {
      const currentUser = localStorage.getItem("user");
      const exists = users.find((u) => u.username === currentUser);
      if (exists) setWriter(currentUser);
    }
  }, [users]);

  useEffect(() => {
    if (status === "Closed") {
      setClosingDate(dayjs().toDate());
    } else {
      setClosingDate(null);
    }
  }, [status]);

  // Carga usuarios para escribir notas

  useEffect(() => {
    if (
      shouldLoadNotas({
        open,
        productId,
        idMuestra,
        idPresupuestoA3Pedido,
      })
    ) {
      loadNotas();
    }
  }, [open, productId, idMuestra, idPresupuestoA3Pedido]);

  const loadNotas = async () => {
    const token = localStorage.getItem("token");
    const { userId } = jwtDecode(token);

    const data = await getNotasByEntityWithUser({
      productId,
      idMuestra,
      idPresupuestoA3Pedido,
      userId,
    });
    setNotas(data);

    // Cargar adjuntos por nota
    const filesMap = {};

    for (const n of data) {
      const files = await getAdjuntosByNota(n.Id);
      filesMap[n.Id] = files;
    }

    setAdjuntos(filesMap);
  };

  const saveNota = async () => {
    if (!canSaveNewNota({ nota, writer, responsible })) return;

    const payload = buildCreateNotaPayload({
      productId,
      idMuestra,
      idPresupuestoA3Pedido,
      nota,
      usuario: localStorage.getItem("user") || "Unknown",
      tipoNota,
      writer,
      responsible,
      status,
      targetDate,
      closingDate,
    });

    const res = await addNotaToEntity(payload);
    const notaId = res?.notaId;
    const hadAttachments = files.length > 0;

    if (hadAttachments && notaId) {
      await uploadAdjuntosNota({
        notaId,
        productId: payload.productId,
        idMuestra: payload.idMuestra,
        idPresupuestoA3Pedido: payload.idPresupuestoA3Pedido,
        files,
      });
    }

    setNota("");
    setFiles([]);
    setTipoNota("Remark");
    setResponsible("");
    setStatus("Open");
    setTargetDate(null);
    setClosingDate(null);

    await loadNotas();
    onClose(
      buildNotasDialogClosePayload({
        changed: true,
        productId: payload.productId,
        idMuestra: payload.idMuestra,
        idPresupuestoA3Pedido: payload.idPresupuestoA3Pedido,
        hasAttachments: hadAttachments,
      }),
    );
  };

  const saveEdit = async (id) => {
    const updatePayload = buildEditNotaUpdatePayload(editNota);
    if (!updatePayload) return;

    await updateNotaEntity(id, updatePayload);

    setEditMode(null);
    setEditNota(null);
    loadNotas();
    onClose(
      buildNotasDialogClosePayload({
        changed: true,
        productId,
        idMuestra,
        idPresupuestoA3Pedido,
      }),
    );
  };

  const confirmDelete = async () => {
    await deleteNotaEntity(notaToDelete);

    setDeleteConfirmOpen(false);
    setNotaToDelete(null);

    loadNotas();
    onClose(
      buildNotasDialogClosePayload({
        changed: true,
        productId,
        idMuestra,
        idPresupuestoA3Pedido,
      }),
    );
  };

  return (
    <>
      {/* MAIN DIALOG */}
      <Dialog
        open={open}
        onClose={() =>
          onClose(
            buildNotasDialogClosePayload({
              changed: false,
              productId,
              idMuestra,
              idPresupuestoA3Pedido,
            }),
          )
        }
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: "hidden",
            border: isMobile ? 0 : 1,
            borderColor: "divider",
            backgroundImage: "none",
            boxShadow: isMobile
              ? "none"
              : theme.palette.mode === "dark"
                ? "0 8px 32px rgba(0,0,0,0.5)"
                : "0 8px 32px rgba(0,0,0,0.1)",
            ...(isMobile
              ? {
                  display: "flex",
                  flexDirection: "column",
                  maxHeight: "100dvh",
                }
              : {}),
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            p: 0,
            background:
              "linear-gradient(135deg, rgba(0,188,212,0.14) 0%, rgba(103,58,183,0.1) 100%)",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 1, display: "block" }}
            >
              CRM
            </Typography>
            <Typography
              component="h2"
              variant="h6"
              sx={{ fontWeight: 800, lineHeight: 1.3, mt: 0.5 }}
            >
              {t("Notas.Notas")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, maxWidth: 600 }}
            >
              {t("Notas.dialogSubtitle")}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            py: { xs: 2, md: 2.5 },
            px: { xs: 1.5, md: 2.5 },
            bgcolor: "background.default",
            borderColor: "divider",
            flex: isMobile ? 1 : undefined,
            minHeight: isMobile ? 0 : undefined,
            overflowY: "auto",
          }}
        >
          <List disablePadding>
            {notas.length === 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: "center",
                  borderRadius: 2,
                  borderStyle: "dashed",
                }}
              >
                <Typography color="text.secondary" variant="body2">
                  {t("Notas.NoNotas")}
                </Typography>
              </Paper>
            )}

            {notas.map((n) => (
              <Box key={n.Id} sx={noteRowSx}>
                {/* Fila de metadata */}
                <Box sx={noteMetaSx}>
                  {editMode === n.Id ? (
                    <>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        label={t("Notes.Tipo")}
                        SelectProps={{ native: true }}
                        value={editNota.tipoNota}
                        onChange={(e) =>
                          setEditNota({ ...editNota, tipoNota: e.target.value })
                        }
                        sx={{
                          ...fieldSx,
                          "& .MuiNativeSelect-select": {
                            fontWeight: 700,
                            color:
                              editNota.tipoNota === "Open Issue"
                                ? "error.main"
                                : "info.main",
                          },
                        }}
                      >
                        <option value="Remark">{t("Notes.remark")}</option>
                        <option value="Open Issue">
                          {t("Notes.Open issue")}
                        </option>
                      </TextField>

                      <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        label={t("Notes.Writer")}
                        SelectProps={{ native: true }}
                        value={editNota.writer}
                        onChange={(e) =>
                          setEditNota({ ...editNota, writer: e.target.value })
                        }
                        sx={fieldSx}
                      >
                        {users.map((u) => (
                          <option key={u.id} value={u.username}>
                            {u.username}
                          </option>
                        ))}
                      </TextField>

                      <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        label={t("Notes.Responsible")}
                        SelectProps={{ native: true }}
                        value={editNota.responsible}
                        onChange={(e) =>
                          setEditNota({
                            ...editNota,
                            responsible: e.target.value,
                          })
                        }
                        sx={fieldSx}
                      >
                        {users.map((u) => (
                          <option key={u.id} value={u.username}>
                            {u.username}
                          </option>
                        ))}
                      </TextField>

                      <TextField
                        select
                        fullWidth
                        size="small"
                        variant="outlined"
                        label={t("Notes.Status")}
                        SelectProps={{ native: true }}
                        value={editNota.status}
                        onChange={(e) =>
                          setEditNota({ ...editNota, status: e.target.value })
                        }
                        sx={fieldSx}
                      >
                        <option value="Open">{t("Notes.Open")}</option>
                        <option value="In Progress">
                          {t("Notes.In Progress")}
                        </option>
                        <option value="Closed">{t("Notes.Closed")}</option>
                      </TextField>

                      <TextField
                        type="date"
                        fullWidth
                        size="small"
                        variant="outlined"
                        label={t("Notes.TargetDate")}
                        InputLabelProps={{ shrink: true }}
                        value={
                          editNota.targetDate
                            ? dayjs(editNota.targetDate).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={(e) =>
                          setEditNota({
                            ...editNota,
                            targetDate: e.target.value
                              ? dayjs(e.target.value).toDate()
                              : null,
                          })
                        }
                        sx={fieldSx}
                      />
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: "bold",
                          color:
                            n.TipoNota === "Open Issue"
                              ? "error.main"
                              : "info.main",
                        }}
                      >
                        {n.TipoNota}
                      </Typography>

                      <Typography variant="caption">{n.Writer}</Typography>
                      <Typography variant="caption">{n.Responsible}</Typography>
                      <Typography variant="caption">{n.Status}</Typography>
                      <Typography variant="caption">
                        {n.TargetDate
                          ? dayjs(n.TargetDate).format("DD/MM/YYYY")
                          : "—"}
                      </Typography>
                      <Typography variant="caption">
                        {n.ClosingDate
                          ? dayjs(n.ClosingDate).format("DD/MM/YYYY")
                          : "—"}
                      </Typography>
                    </>
                  )}

                  <Stack
                    direction="row"
                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    spacing={0.75}
                    sx={{ mt: 1 }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditMode(n.Id);
                        setEditNota({
                          nota: n.Nota,
                          tipoNota: n.TipoNota,
                          writer: n.Writer,
                          responsible: n.Responsible,
                          status: n.Status,
                          targetDate: n.TargetDate,
                          closingDate: n.ClosingDate,
                        });
                      }}
                      sx={{
                        border: 1,
                        borderColor: "info.main",
                        color: "info.main",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setNotaToDelete(n.Id);
                        setDeleteConfirmOpen(true);
                      }}
                      sx={{
                        border: 1,
                        borderColor: "error.main",
                        color: "error.main",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>

                <Box sx={noteBodySx}>
                  {/* Texto de la nota */}
                  {editMode === n.Id ? (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        minRows={isMobile ? 5 : 8.8}
                        variant="outlined"
                        value={editNota.nota}
                        onChange={(e) =>
                          setEditNota({ ...editNota, nota: e.target.value })
                        }
                        sx={fieldSx}
                      />
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ mt: 1 }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => saveEdit(n.Id)}
                          fullWidth={isMobile}
                        >
                          {t("Notas.GuardarNota")}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setEditMode(null)}
                          color="error"
                          variant="text"
                          fullWidth={isMobile}
                        >
                          {t("Notas.CancelarNota")}
                        </Button>
                      </Stack>
                    </>
                  ) : (
                    <Typography variant="body2">{n.Nota}</Typography>
                  )}
                  {adjuntos[n.Id]?.length > 0 && (
                    <Box
                      sx={{
                        mt: 1.5,
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                      }}
                    >
                      {adjuntos[n.Id].map((f) => {
                        const isImage = isImageAdjunto(f.MimeType);
                        const fileUrl = adjuntoFileUrl(API_URL, f.FilePath);

                        return (
                          <Box
                            key={f.Id}
                            sx={{
                              width: 110,
                              border: 1,
                              borderColor: "divider",
                              borderRadius: 1.5,
                              p: 0.5,
                              textAlign: "center",
                              bgcolor: "background.paper",
                            }}
                          >
                            {/* 🖼️ MINI VISOR */}
                            {isImage && (
                              <Box
                                component="a"
                                href={fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: "block",
                                  mb: 0.5,
                                }}
                              >
                                <img
                                  src={fileUrl}
                                  alt={f.FileName}
                                  style={{
                                    width: "100%",
                                    height: 80,
                                    objectFit: "cover",
                                    borderRadius: 4,
                                  }}
                                />
                              </Box>
                            )}

                            {/* 📎 LINK */}
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                wordBreak: "break-word",
                              }}
                            >
                              <a
                                href={fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: "none" }}
                              >
                                📎 {f.FileName}
                              </a>
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </List>

          <Divider sx={{ my: 3 }} />

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: 2,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
          <Typography
            variant="subtitle1"
            sx={{ mb: { xs: 1.5, md: 2 }, fontWeight: 700 }}
          >
            {t("Notas.AddNota")}
          </Typography>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1.5, md: 1.25 }}
            alignItems="stretch"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: { xs: "none", md: "0 0 34%" },
                width: { xs: "100%", md: "auto" },
                minWidth: 0,
              }}
            >
              {/* Tipo */}

              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label={t("Notes.Tipo")}
                SelectProps={{ native: true }}
                value={tipoNota}
                onChange={(e) => setTipoNota(e.target.value)}
                sx={{
                  ...fieldSx,
                  "& .MuiNativeSelect-select": { padding: "8px 14px" },
                }}
              >
                <option value="Remark">{t("Notes.remark")}</option>
                <option value="Open Issue">{t("Notes.Open issue")}</option>
              </TextField>

              {/* Status */}

              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label={t("Notes.Status")}
                SelectProps={{ native: true }}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                sx={{
                  ...fieldSx,
                  "& .MuiNativeSelect-select": { padding: "8px 14px" },
                }}
              >
                <option value="Open">{t("Notes.Open")}</option>
                <option value="In Progress">{t("Notes.In Progress")}</option>
                <option value="Closed">{t("Notes.Closed")}</option>
              </TextField>

              {/* Writer */}

              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label={t("Notes.Writer")}
                SelectProps={{ native: true }}
                value={writer}
                onChange={(e) => setWriter(e.target.value)}
                sx={{
                  ...fieldSx,
                  "& .MuiNativeSelect-select": { padding: "8px 14px" },
                }}
              >
                <option value="">—</option>
                {users.map((u) => (
                  <option key={u.id} value={u.username}>
                    {u.username}
                  </option>
                ))}
              </TextField>

              {/* Responsible */}

              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label={t("Notes.Responsible")}
                SelectProps={{ native: true }}
                value={responsible}
                onChange={(e) => setResponsible(e.target.value)}
                sx={{
                  ...fieldSx,
                  "& .MuiNativeSelect-select": { padding: "8px 14px" },
                }}
              >
                <option value="">—</option>
                {users.map((u) => (
                  <option key={u.id} value={u.username}>
                    {u.username}
                  </option>
                ))}
              </TextField>

              {/* Target date */}

              <TextField
                type="date"
                fullWidth
                size="small"
                variant="outlined"
                label={t("Notes.TargetDate")}
                InputLabelProps={{ shrink: true }}
                value={targetDate ? dayjs(targetDate).format("YYYY-MM-DD") : ""}
                onChange={(e) =>
                  setTargetDate(
                    e.target.value ? dayjs(e.target.value).toDate() : null,
                  )
                }
                sx={fieldSx}
              />
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                "& .MuiNativeSelect-select": { padding: "8px 14px" },
              }}
            >
              <TextField
                fullWidth
                multiline
                minRows={isMobile ? 5 : 7.2}
                size="small"
                variant="outlined"
                label={t("Notas.AddNota")}
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                sx={{ ...fieldSx, mb: { xs: 1.25, md: 1 } }}
              />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ width: "100%" }}
              >
                <Button
                  variant="contained"
                  onClick={saveNota}
                  disabled={!canSaveNewNota({ nota, writer, responsible })}
                  fullWidth
                  sx={{ flex: { sm: 1 } }}
                >
                  {t("Notas.GuardarNota")}
                </Button>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ flex: { sm: 1 } }}
                >
                  {t("Notas.UploadFiles")}
                  <input
                    hidden
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => setFiles([...e.target.files])}
                  />
                </Button>
              </Stack>
              {files.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "text.secondary", mt: 1 }}
                >
                  {t("Notas.filesSelected", { count: files.length })}
                </Typography>
              )}
            </Box>
          </Stack>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 1.5, md: 2.5 },
            py: { xs: 1.5, md: 2 },
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            fullWidth={isMobile}
            onClick={() =>
              onClose(
                buildNotasDialogClosePayload({
                  changed: false,
                  productId,
                  idMuestra,
                  idPresupuestoA3Pedido,
                }),
              )
            }
          >
            {t("Notas.CerrarNotas")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, border: 1, borderColor: "divider" } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {t("Notas.EliminarNota")}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "divider" }}>
          <Typography color="text.secondary">
            {t("Notas.ConfirmDeleteNota")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>
            {t("Notas.CancelarNota")}
          </Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            {t("Notas.EliminarNota")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotasDialog;
